import { useScreenSize } from '@/themes/media-query'
import { Suspense, useCallback, useRef, useState } from 'react'
import { Await, useLoaderData } from 'react-router-typesafe'
import { CellPreparedEvent, ExportingEvent } from 'devextreme/ui/data_grid'
import DataGrid, {
  Column,
  Export,
  HeaderFilter,
  Scrolling,
  StateStoring,
  DataGridRef,
  FilterRow,
  FilterPanel,
  Pager,
} from 'devextreme-react/data-grid'
import { exportDataGrid } from 'devextreme/excel_exporter'
import saveAs from 'file-saver'
import { Workbook } from 'exceljs'
import { gridTotalItemsLabel } from '@/constants'
import LoadingPanel from '@/components/loading-panel/LoadingPanel'
import {
  absenceAnomaliesPageRouteLoader,
  FluidaAbsenceAuthorization,
  FluidaAbsenceCategory,
  FluidaAbsenceCheckContractFailure,
  FluidaAbsenceCheckIssue,
  FluidaAbsenceCheckResult,
  FluidaAbsenceIssueType,
  getFluidaAbsenceAnomalies,
} from '@/routes/hours/anomalies/absences/AbsenceAnomaliesPage.route'
import { getPreviousWorkWeek } from '@/routes/hours/anomalies/anomalies.utils'
import { GridCellColor } from '@/enums'
import Toolbar, { Item as ToolbarItem } from 'devextreme-react/toolbar'
import { Button, DateRangeBox } from 'devextreme-react'
import { DateRangeBoxRef, DateRangeBoxTypes } from 'devextreme-react/cjs/date-range-box'
import { ClickEvent } from 'devextreme/ui/button'
import notify from 'devextreme/ui/notify'

const issueTypeLabels: Record<FluidaAbsenceIssueType, string> = {
  Missing: 'Assenza mancante',
  DurationMismatch: 'Durata non coerente',
}

const categoryLabels: Record<FluidaAbsenceCategory, string> = {
  Permit: 'permesso',
  Holiday: 'ferie',
  Mixed: 'ferie/permesso',
}

const authorizationLabels: Record<FluidaAbsenceAuthorization, string> = {
  Authorized: 'Approvata',
  RequestedNotAuthorized: 'Richiesta non approvata',
  NotRequestedNotAuthorized: 'Assenza senza richiesta',
  Mixed: 'Richieste non tutte approvate',
}

// Ore arrotondate per difetto al quarto d'ora, come nel file anomalie assenze dell'export ore.
const toQuarterHours = (minutes: number) => Math.floor((minutes / 60) * 4) / 4

interface AbsenceAnomalyRow extends FluidaAbsenceCheckIssue {
  typeLabel: string
  categoryLabel: string
  authorizationLabel: string
  fluidaHours: number
  qsWebAdminHours: number
  missingHours: number
}

const toRows = (issues: FluidaAbsenceCheckIssue[]): AbsenceAnomalyRow[] =>
  issues.map((issue) => {
    const fluidaHours = toQuarterHours(issue.minimumRequiredMinutes)
    const qsWebAdminHours = toQuarterHours(issue.qsWebAdminMinutes)
    return {
      ...issue,
      typeLabel: issueTypeLabels[issue.type],
      categoryLabel: categoryLabels[issue.category],
      authorizationLabel: authorizationLabels[issue.authorization],
      fluidaHours,
      qsWebAdminHours,
      missingHours: fluidaHours - qsWebAdminHours,
    }
  })

const ContractFailuresWarning = ({ failures }: { failures: FluidaAbsenceCheckContractFailure[] }) =>
  failures.length > 0 ? (
    <div
      style={{
        margin: '0.5rem 0',
        padding: '0.5rem 1rem',
        color: 'black',
        backgroundColor: GridCellColor.YELLOW,
        borderRadius: '4px',
      }}
    >
      <b>Controllo incompleto: per i seguenti utenti non è stato possibile verificare le assenze su Fluida.</b>
      <ul style={{ margin: '0.25rem 0 0 0' }}>
        {failures.map((failure) => (
          <li key={failure.contractId}>
            {failure.userName?.trim() ? failure.userName : failure.userEmail}: {failure.error}
          </li>
        ))}
      </ul>
    </div>
  ) : null

const AbsenceAnomaliesPage = () => {
  const { minDate, maxDate, getAbsenceAnomalies } = useLoaderData<typeof absenceAnomaliesPageRouteLoader>()
  const refDateRangeBox = useRef<DateRangeBoxRef>(null)
  const [absenceAnomalies, setAbsenceAnomalies] = useState<FluidaAbsenceCheckResult | null>(null)
  const initialValue: [Date, Date] = [minDate, maxDate]
  const currentScreenSize = useScreenSize()
  const getGridHeight = useCallback(() => {
    let h = '79vh'
    if (currentScreenSize.isMedium) {
      h = '75vh'
    } else if (currentScreenSize.isSmall) {
      h = '120vh'
    } else if (currentScreenSize.isXSmall) {
      h = '75vh'
    }
    return h
  }, [currentScreenSize])
  const gridRef = useRef<DataGridRef>(null)

  const onExporting = (e: ExportingEvent) => {
    const workbook = new Workbook()
    const worksheet = workbook.addWorksheet('AbsenceAnomalies')
    const component = e.component
    exportDataGrid({ component, worksheet }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'AbsenceAnomalies.xlsx')
      })
    })
  }

  const onCellPrepared = (e: CellPreparedEvent) => {
    if (e.rowType === 'data' && e.column.dataField === 'typeLabel' && e.data) {
      switch (e.data.type) {
        case 'Missing': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.SALMON}`
          break
        }
        case 'DurationMismatch': {
          e.cellElement.style.cssText = `${e.cellElement.style.cssText}color: black; background-color: ${GridCellColor.YELLOW}`
          break
        }
      }
    }
  }

  return (
    <>
      <Suspense fallback={<LoadingPanel />}>
        <Await resolve={getAbsenceAnomalies}>
          {(values) => {
            const result = absenceAnomalies ?? values.data
            return (
              <>
                <div>
                  <Toolbar>
                    <ToolbarItem location="before" locateInMenu="never">
                      <DateRangeBox
                        ref={refDateRangeBox}
                        elementAttr={{ class: 'irregular-daterange' }}
                        width={currentScreenSize.isLarge || currentScreenSize.isMedium ? '350px' : '250px'}
                        defaultValue={initialValue}
                        startDateLabel="Data Inizio"
                        endDateLabel="Data Fine"
                        applyValueMode="useButtons"
                        buttons={[
                          // 'dropDown' mantiene il calendario di default per la selezione manuale del range.
                          'clear',
                          'dropDown',
                          // Button custom: preseleziona la settimana lavorativa precedente (lun-ven).
                          {
                            name: 'previousWeek',
                            location: 'after',
                            options: {
                              icon: 'event',
                              hint: 'Imposta la settimana lavorativa precedente',
                              stylingMode: 'text',
                              onClick: () => {
                                const [start, end] = getPreviousWorkWeek(new Date())
                                refDateRangeBox.current?.instance().option('value', [start, end])
                              },
                            },
                          },
                        ]}
                        onValueChanged={(e: DateRangeBoxTypes.ValueChangedEvent) => {
                          if (e.value[1] === null)
                            refDateRangeBox.current?.instance().option('value', [e.value[0], e.value[0]])
                        }}
                      />
                    </ToolbarItem>
                    <ToolbarItem
                      location="after"
                      locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
                      showText="inMenu"
                    >
                      <Button
                        text="Reset"
                        icon="undo"
                        useSubmitBehavior={false}
                        width={'150px'}
                        onClick={(e: ClickEvent) => {
                          refDateRangeBox.current?.instance().option('value', initialValue)
                          setAbsenceAnomalies(values.data)
                        }}
                      />
                    </ToolbarItem>
                    <ToolbarItem
                      location="after"
                      locateInMenu={currentScreenSize.isSmall || currentScreenSize.isXSmall ? 'always' : 'auto'}
                      showText="inMenu"
                    >
                      <Button
                        text="Ricarica"
                        icon="refresh"
                        useSubmitBehavior={false}
                        width={'150px'}
                        onClick={(e: ClickEvent) => {
                          gridRef?.current?.instance().beginCustomLoading('Recupero anomalie in corso....')
                          const range: Date[] = refDateRangeBox.current?.instance().option().value
                          getFluidaAbsenceAnomalies(range[0], range[1])
                            .then((response) => {
                              setAbsenceAnomalies(response.data)
                            })
                            .catch((error) => {
                              notify(`Errore recupero anomalie assenze. Dettagli: ${error}`, 'error', 3000)
                            })
                            .finally(() => {
                              gridRef?.current?.instance().endCustomLoading()
                            })
                        }}
                      />
                    </ToolbarItem>
                  </Toolbar>
                  <ContractFailuresWarning failures={result.contractFailures} />
                  <DataGrid
                    id={'absence-anomalies-datagrid'}
                    height={getGridHeight()}
                    dataSource={toRows(result.issues)}
                    noDataText="Nessuna anomalia trovata"
                    className={'dx-card wide-card'}
                    showBorders={false}
                    showColumnLines={true}
                    wordWrapEnabled={false}
                    showRowLines={true}
                    rowAlternationEnabled={true}
                    allowColumnResizing={currentScreenSize.isLarge || currentScreenSize.isMedium}
                    allowColumnReordering={currentScreenSize.isLarge || currentScreenSize.isMedium}
                    width="100%"
                    onExporting={onExporting}
                    ref={gridRef}
                    columnHidingEnabled={
                      currentScreenSize.isSmall || currentScreenSize.isXSmall || currentScreenSize.isMedium
                    }
                    onCellPrepared={onCellPrepared}
                    grouping={{
                      // Fondamentale per il raggruppamento veloce!!!
                      autoExpandAll: true,
                    }}
                    groupPanel={{
                      visible: currentScreenSize.isLarge || currentScreenSize.isMedium,
                      emptyPanelText: 'Trascina qui una colonna per raggruppare',
                    }}
                  >
                    <FilterRow visible={true} />
                    <FilterPanel visible={true} />
                    <HeaderFilter visible={true} />
                    <Export enabled={true} />
                    <Scrolling mode="virtual" />
                    <Pager
                      visible={true}
                      allowedPageSizes={'auto'}
                      displayMode={'compact'}
                      showPageSizeSelector={false}
                      showInfo={true}
                      showNavigationButtons={false}
                      infoText={gridTotalItemsLabel}
                    />
                    <StateStoring
                      enabled={true}
                      type="localStorage"
                      storageKey={'absence-anomalies-datagrid'}
                      savingTimeout={50}
                    />
                    <Column dataField={'userName'} caption="DIPENDENTE" dataType="string"></Column>
                    <Column dataField="day" caption="DATA" dataType="date" allowHeaderFiltering={false}></Column>
                    <Column dataField={'typeLabel'} caption="TIPOLOGIA" dataType="string"></Column>
                    <Column dataField={'categoryLabel'} caption="CATEGORIA" dataType="string"></Column>
                    <Column dataField={'authorizationLabel'} caption="AUTORIZZAZIONE" dataType="string"></Column>
                    <Column
                      dataField={'fluidaHours'}
                      caption="ORE FLUIDA"
                      dataType="number"
                      allowHeaderFiltering={false}
                    ></Column>
                    <Column
                      dataField={'qsWebAdminHours'}
                      caption="ORE WEBADMIN"
                      dataType="number"
                      allowHeaderFiltering={false}
                    ></Column>
                    <Column
                      dataField={'missingHours'}
                      caption="ORE MANCANTI"
                      dataType="number"
                      allowHeaderFiltering={false}
                    ></Column>
                  </DataGrid>
                </div>
              </>
            )
          }}
        </Await>
      </Suspense>
    </>
  )
}

export default AbsenceAnomaliesPage
